import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalHeaderContent,
  ModalIconWrapper,
  ModalTitle,
  ModalSubtitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  ModalCancelButton,
  ModalSubmitButton,
  ModalLabel,
  ModalInputWrapper,
  ModalInput,
  Spinner,
} from "../NewCase/ModalStyles";
import { DueDiligenceCreditCosts, SummaryActions, SummaryBox, SummaryLink } from "../NewCase/PaymentSection";
import { DUE_DILIGENCE_TIER_LEVELS, formatMoney, planNameKey } from "../../../Common/Utils/dueDiligencePlans";
import { FiX, FiTrendingUp, FiArrowRight, FiTag, FiCheck } from "react-icons/fi";
import { DueDiligenceCase, DueDiligenceTier } from "../../../Common/Types-DTOs/DueDiligence";
import { BusinessCreditsDTO } from "../../../Common/Types-DTOs/ClientUser";
import { TierSelector } from "./TierSelector";
import { useDueDiligenceUpsellPayment } from "../../../Common/Hooks/useDueDiligence/useDueDiligenceUpsellPayment";
import { useDueDiligence } from "../../../Common/Hooks/useDueDiligence";
import { useBusiness } from "../../../Common/Hooks/useBusiness";
import { isErr } from "../../../Common/Utils/Result";
import { eventBus } from "../../../Common/EventBus";
import { roundCredits } from "../../../Common/Utils/credits";
import { Notification } from "../../../Components/Notifications";

const TIER_ORDER: DueDiligenceTier[] = ["basic", "professional", "premium"];

/** What the backend may answer when upgrading with credits, as the user reads it. */
const UPGRADE_ERROR_KEYS: Record<string, string> = {
  INSUFFICIENT_CREDITS: "dueDiligence.upsellModal.errors.insufficientCredits",
  DUE_DILIGENCE_TIER_NOT_IN_PLAN: "dueDiligence.upsellModal.errors.tierNotInPlan",
};

/** Why the upgrade by card couldn't be priced, as the user reads it. */
const QUOTE_ERROR_KEYS: Record<string, string> = {
  DUE_DILIGENCE_CASE_PAYMENT_NOT_FOUND: "dueDiligence.upsellModal.errors.paymentNotFound",
};
const INVALID_PROMOTION_CODE = "DUE_DILIGENCE_INVALID_PROMOTION_CODE";

/** The euros each upgrade costs by card, or the error code of why not. */
type QuoteOutcome = { quotes: Partial<Record<DueDiligenceTier, number>> } | { errorCode: string };

const PromotionCodeRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 6px;

  ${ModalInput} {
    padding-top: 0.6rem;
    padding-bottom: 0.6rem;
    font-size: 0.875rem;
  }
`;

const ApplyCodeButton = styled(ModalCancelButton)`
  flex-shrink: 0;
`;

const PromotionCodeNote = styled.div<{ $error?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 0.78rem;
  color: ${(p) => (p.$error ? "#D4443A" : "#047857")};
`;

/** The report as it is, so the difference to pay makes sense. */
const CurrentReport = styled.div`
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background-color: #f9fafb;
  font-size: 0.8rem;
  color: #6b7280;

  b {
    color: #111827;
  }
`;

/** Why credits can't pay an upgrade, if they can't. */
export type UpgradeShortfall = { kind: "notInPlan" } | { kind: "missing"; credits: number } | null;

/**
 * Whether the business credits pay an upgrade costing the given credits.
 * @param credits - The business balance and plan.
 * @param toTier - The tier the case is upgraded to.
 * @param cost - What the upgrade costs in credits.
 * @returns Null when they pay it; else why not.
 */
export const upgradeShortfall = (
  credits: BusinessCreditsDTO,
  toTier: DueDiligenceTier,
  cost: number
): UpgradeShortfall => {
  const allowedTiers = credits.dueDiligencePlan?.allowedTiers;
  if (allowedTiers && !allowedTiers.includes(toTier)) {
    return { kind: "notInPlan" };
  }
  if (cost > credits.balance) {
    return { kind: "missing", credits: roundCredits(cost - credits.balance) };
  }
  return null;
};

interface UpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  currentTier: DueDiligenceTier;
  pricing?: { basic: number | null; professional: number | null; premium: number | null } | null;
  creditCosts?: DueDiligenceCreditCosts | null;
  /** The tier picked where the upgrade was asked for, e.g. a report pill; else the next one. */
  initialTier?: DueDiligenceTier;
  /** The case once upgraded with credits, as saved. */
  onUpgraded?: (upgradedCase: DueDiligenceCase) => void;
}

export const UpsellModal: React.FC<UpsellModalProps> = ({
  isOpen,
  onClose,
  caseId,
  currentTier,
  pricing,
  creditCosts,
  initialTier,
  onUpgraded,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { business, resyncBusiness } = useBusiness();
  const { upgradeWithCredits, getUpsellQuote } = useDueDiligence();
  const availableTiers = TIER_ORDER.slice(TIER_ORDER.indexOf(currentTier) + 1);
  const [selectedTier, setSelectedTier] = useState<DueDiligenceTier>(availableTiers[0]);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [quotes, setQuotes] = useState<Partial<Record<DueDiligenceTier, number>>>({});
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const [isCodeInvalid, setIsCodeInvalid] = useState(false);
  const [isApplyingCode, setIsApplyingCode] = useState(false);
  const { isPayingNow, handleUpsellPayNow } = useDueDiligenceUpsellPayment(caseId);
  const credits = business?.credits;
  const plan = credits?.dueDiligencePlan;
  // Subscribers upgrade with credits only; without a subscription, in euros
  // through Stripe, with no plan offered to pay it
  const withCredits = Boolean(plan);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedTier(initialTier && availableTiers.includes(initialTier) ? initialTier : availableTiers[0]);
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTier]);

  // By card, the backend prices each upgrade: the code goes on the full price
  // of the destination tier, and what the case already paid comes off
  const fetchQuotes = async (promotionCode?: string): Promise<QuoteOutcome> => {
    const results = await Promise.all(availableTiers.map((tier) => getUpsellQuote(caseId, tier, promotionCode)));
    const fetched: Partial<Record<DueDiligenceTier, number>> = {};
    for (const [index, result] of results.entries()) {
      if (isErr(result)) return { errorCode: result.error.errorCode };
      fetched[availableTiers[index]] = result.value.amountToPay;
    }
    return { quotes: fetched };
  };

  const showQuotes = (outcome: QuoteOutcome) => {
    if ("errorCode" in outcome) {
      setQuoteError(outcome.errorCode);
      return;
    }
    setQuoteError(null);
    setQuotes(outcome.quotes);
  };

  useEffect(() => {
    if (!isOpen || withCredits || availableTiers.length === 0) return;
    setQuotes({});
    setQuoteError(null);
    setCodeInput("");
    setAppliedCode(null);
    setIsCodeInvalid(false);
    let cancelled = false;
    fetchQuotes().then((outcome) => {
      if (!cancelled) showQuotes(outcome);
    });
    return () => { cancelled = true; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, withCredits, caseId, currentTier]);

  if (!isOpen || availableTiers.length === 0) return null;

  const applyCode = async () => {
    const code = codeInput.trim();
    if (!code || isApplyingCode) return;
    setIsApplyingCode(true);
    const outcome = await fetchQuotes(code);
    setIsApplyingCode(false);
    if ("errorCode" in outcome && outcome.errorCode === INVALID_PROMOTION_CODE) {
      setIsCodeInvalid(true);
      return;
    }
    setIsCodeInvalid(false);
    setAppliedCode(code);
    showQuotes(outcome);
  };

  const removeCode = async () => {
    setAppliedCode(null);
    setCodeInput("");
    showQuotes(await fetchQuotes());
  };

  const money = (amount: number) => formatMoney(amount, "eur", i18n.language);

  // In credits, an upgrade costs the difference between both tiers; credits
  // never turn into euros nor back
  const creditDifference = (tier: DueDiligenceTier) =>
    creditCosts ? creditCosts[tier] - creditCosts[currentTier] : undefined;

  const priceLabels: Partial<Record<DueDiligenceTier, string>> = {};
  for (const tier of availableTiers) {
    const euros = quotes[tier];
    const creditsCost = creditDifference(tier);
    if (withCredits && creditsCost !== undefined) {
      priceLabels[tier] = t("dueDiligence.upsellModal.cards.credits", { count: creditsCost });
    } else if (!withCredits && euros !== undefined) {
      priceLabels[tier] = money(euros);
    }
  }

  const selectedEuros = quotes[selectedTier];
  const selectedCredits = creditDifference(selectedTier);
  const shortfall =
    withCredits && credits && selectedCredits !== undefined
      ? upgradeShortfall(credits, selectedTier, selectedCredits)
      : null;
  const canUpgradeWithCredits = withCredits && selectedCredits !== undefined && !shortfall;

  const goToPlans = () => {
    onClose();
    navigate("/due-diligence-plans");
  };

  const summary = (() => {
    if (plan && credits && selectedCredits !== undefined) {
      if (!shortfall) {
        return (
          <SummaryBox $tone="ok">
            {t("dueDiligence.upsellModal.summary.deducted", {
              count: selectedCredits,
              left: roundCredits(credits.balance - selectedCredits),
            })}
          </SummaryBox>
        );
      }
      const reason =
        shortfall.kind === "notInPlan"
          ? t("dueDiligence.upsellModal.summary.notInPlan", { plan: t(planNameKey(plan.lookupKey)) })
          : t("dueDiligence.upsellModal.summary.missing", { count: shortfall.credits });
      const renewal = t("dueDiligence.newCaseModal.payment.summary.renews", {
        date: new Date(plan.paidUntil).toLocaleDateString(i18n.language, { day: "numeric", month: "short" }),
      });
      // Topping up credits will join "Cambiar de plan" here once it exists
      return (
        <SummaryBox $tone="warn">
          <span>
            {reason} {renewal}
          </span>
          <SummaryActions>
            <SummaryLink type="button" onClick={goToPlans}>
              {t("dueDiligence.newCaseModal.payment.summary.changePlan")}
            </SummaryLink>
          </SummaryActions>
        </SummaryBox>
      );
    }

    if (!withCredits && quoteError) {
      return (
        <SummaryBox $tone="warn">
          {t(QUOTE_ERROR_KEYS[quoteError] ?? "dueDiligence.upsellModal.errors.quoteFailed")}
        </SummaryBox>
      );
    }

    if (!withCredits && selectedEuros !== undefined) {
      return (
        <SummaryBox $tone="info">
          {t("dueDiligence.upsellModal.summary.byCard", { price: money(selectedEuros) })}
        </SummaryBox>
      );
    }

    return null;
  })();

  const handleUpgradeWithCredits = async () => {
    if (!canUpgradeWithCredits || isUpgrading) return;
    setIsUpgrading(true);
    const result = await upgradeWithCredits(caseId, selectedTier);
    setIsUpgrading(false);
    resyncBusiness();
    if (isErr(result)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          t(UPGRADE_ERROR_KEYS[result.error.errorCode] ?? "dueDiligence.upsellModal.errors.upgradeFailed")
        )
      );
      return;
    }
    eventBus.emit(
      "NOTIFICATION",
      Notification.buildNotification(
        "success",
        t("dueDiligence.upsellModal.upgraded", { level: DUE_DILIGENCE_TIER_LEVELS[selectedTier] })
      )
    );
    onUpgraded?.(result.value.case);
    onClose();
  };

  const isBusy = isPayingNow || isUpgrading;

  const submitLabel = withCredits
    ? t(isUpgrading ? "dueDiligence.upsellModal.upgradingButton" : "dueDiligence.upsellModal.upgradeWithCreditsButton")
    : isPayingNow
      ? t("dueDiligence.upsellModal.payingButton")
      : selectedEuros !== undefined
        ? t("dueDiligence.upsellModal.payByCardButton", { price: money(selectedEuros) })
        : t("dueDiligence.upsellModal.payButton");

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const modalContent = (
    <ModalOverlay onClick={handleBackdropClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalHeaderContent>
            <ModalIconWrapper>
              <FiTrendingUp size={24} />
            </ModalIconWrapper>
            <div>
              <ModalTitle>{t("dueDiligence.upsellModal.title")}</ModalTitle>
              <ModalSubtitle>
                {t("dueDiligence.upsellModal.subtitle")}
              </ModalSubtitle>
            </div>
          </ModalHeaderContent>
          <ModalCloseButton onClick={onClose}>
            <FiX size={20} />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          <CurrentReport>
            {t("dueDiligence.upsellModal.currentReport")}{" "}
            <b>
              {t("dueDiligence.upsellModal.tierName", {
                level: DUE_DILIGENCE_TIER_LEVELS[currentTier],
                name: t(`dueDiligence.tierSelector.tiers.${currentTier}.title`),
              })}
            </b>
          </CurrentReport>
          <ModalLabel>{t("dueDiligence.upsellModal.upgradeTo")}</ModalLabel>
          <div style={{ marginTop: "0.5rem" }}>
            <TierSelector
              selectedTier={selectedTier}
              onTierChange={setSelectedTier}
              pricing={pricing}
              tiers={availableTiers}
              priceLabels={priceLabels}
            />
          </div>
          {!withCredits && (
            <div style={{ marginTop: "14px" }}>
              <ModalLabel htmlFor="upsell-promotion-code">{t("dueDiligence.upsellModal.promotionCode.label")}</ModalLabel>
              {appliedCode ? (
                <PromotionCodeNote>
                  <FiCheck size={14} />
                  {t("dueDiligence.upsellModal.promotionCode.applied", { code: appliedCode })}
                  <SummaryLink type="button" onClick={removeCode}>
                    {t("dueDiligence.upsellModal.promotionCode.remove")}
                  </SummaryLink>
                </PromotionCodeNote>
              ) : (
                <>
                  <PromotionCodeRow>
                    <ModalInputWrapper style={{ flex: 1 }}>
                      <FiTag className="input-icon" size={16} />
                      <ModalInput
                        id="upsell-promotion-code"
                        value={codeInput}
                        $hasError={isCodeInvalid}
                        placeholder={t("dueDiligence.upsellModal.promotionCode.placeholder")}
                        onChange={(e) => {
                          setCodeInput(e.target.value);
                          setIsCodeInvalid(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") applyCode();
                        }}
                      />
                    </ModalInputWrapper>
                    <ApplyCodeButton type="button" onClick={applyCode} disabled={!codeInput.trim() || isApplyingCode}>
                      {t(
                        isApplyingCode
                          ? "dueDiligence.upsellModal.promotionCode.applying"
                          : "dueDiligence.upsellModal.promotionCode.apply"
                      )}
                    </ApplyCodeButton>
                  </PromotionCodeRow>
                  {isCodeInvalid && (
                    <PromotionCodeNote $error>{t("dueDiligence.upsellModal.promotionCode.invalid")}</PromotionCodeNote>
                  )}
                </>
              )}
            </div>
          )}
          {summary}
        </ModalBody>

        <ModalFooter>
          <div style={{ fontSize: "0.75rem", color: "#9CA3AF" }}>
            {t("dueDiligence.upsellModal.priceDifferenceNote")}
          </div>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <ModalCancelButton onClick={onClose} disabled={isBusy}>
              {t("dueDiligence.upsellModal.cancelButton")}
            </ModalCancelButton>
            <ModalSubmitButton
              onClick={
                withCredits ? handleUpgradeWithCredits : () => handleUpsellPayNow(selectedTier, appliedCode ?? undefined)
              }
              disabled={isBusy || (withCredits ? !canUpgradeWithCredits : quoteError !== null || isApplyingCode)}
            >
              {submitLabel}
              {isBusy ? <Spinner /> : <FiArrowRight size={16} />}
            </ModalSubmitButton>
          </div>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
};
